import { FileText, Loader2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { type CandidateRecord } from '../../lib/client.ts';
import { uploadCandidateDocument } from '../../lib/documents.ts';
import { Button } from '../ui/button.tsx';
import { useDocumentViewer } from './document-viewer.tsx';

/**
 * The Resume column (RELAY-942/943, TS-1896): ONE button, so the column stays narrow. A
 * candidate with a resume — a CRM document or, until it has one, its RecruitCRM link
 * (`resumeFileLink`) — gets View resume (the file name in its tooltip): a PDF opens in the
 * side-panel viewer, anything else in a new tab (a presigned URL, audited server-side as
 * `document.download`). A candidate without one gets Upload, the init → PUT → complete flow in
 * `lib/documents.ts`, after which the API has made it the candidate's resume. Replacing a
 * resume is on the candidate panel and the form, not here.
 */
export function ResumeCell({
  candidate,
  canUpload,
  canDownload,
  onChanged,
  onError,
}: {
  candidate: Pick<CandidateRecord, 'id' | 'resumeDocument' | 'resumeFileLink' | 'resumeFilename'>;
  canUpload: boolean;
  canDownload: boolean;
  onChanged: () => void | Promise<void>;
  onError: (message: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const doc = candidate.resumeDocument;
  const viewer = useDocumentViewer(onError);
  const resumeName = doc?.filename ?? candidate.resumeFilename ?? 'Resume (RecruitCRM)';
  const upload = async (file: File) => {
    setBusy(true);
    try {
      await uploadCandidateDocument(candidate.id, file, 'resume');
      await onChanged();
    } catch (err) {
      onError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex items-center"
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {doc || candidate.resumeFileLink ? (
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label={`View resume: ${resumeName}`}
          title={`View resume: ${resumeName}`}
          disabled={!canDownload}
          onClick={() => (doc ? viewer.open(doc) : viewer.openResumeLink(candidate))}
          data-testid="resume-view"
        >
          <FileText className="size-5" />
        </Button>
      ) : canUpload ? (
        <>
          <input
            ref={input}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,application/pdf"
            aria-label="Upload resume"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void upload(f);
            }}
          />
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="Upload resume"
            title="Upload resume"
            disabled={busy}
            onClick={() => input.current?.click()}
          >
            {busy ? <Loader2 className="size-5 animate-spin" /> : <Upload className="size-5" />}
          </Button>
        </>
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
      {viewer.viewer}
    </div>
  );
}
